import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Copy, ExternalLink, Layers, Users, Trash2, Download, Mail, Phone, CheckCircle2, Clock } from "lucide-react";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function toCsv(rows) {
  const header = ["Name", "Email", "WhatsApp", "Amount", "Status", "Enrolled at"];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push([
      JSON.stringify(r.name || ""),
      JSON.stringify(r.email || ""),
      JSON.stringify(`${r.country_code || ""} ${r.whatsapp || ""}`.trim()),
      String(r.amount || 0),
      JSON.stringify(r.payment_status || ""),
      JSON.stringify(r.created_at || ""),
    ].join(","));
  });
  return lines.join("\n");
}

export default function CourseDetail() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollStats, setEnrollStats] = useState({ count: 0, paid_count: 0, revenue: 0 });
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/courses/${id}`);
      setCourse(data);
      if (data.slug) {
        const { data: pub } = await api.get(`/public/courses/${data.slug}`);
        setModules(pub.modules || []);
      }
      const { data: enr } = await api.get(`/courses/${id}/enrollments`);
      setEnrollments(enr.items || []);
      setEnrollStats({ count: enr.count || 0, paid_count: enr.paid_count || 0, revenue: enr.revenue || 0 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [id]);

  const copy = () => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${course.slug}`);
    toast.success("Landing link copied");
  };

  const remove = async () => {
    if (!window.confirm("Delete this course? Enrollments will be cleaned up.")) return;
    try {
      await api.delete(`/courses/${id}`);
      toast.success("Course deleted");
      window.location.href = "/app/courses";
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return enrollments;
    const q = query.trim().toLowerCase();
    return enrollments.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.whatsapp || "").toLowerCase().includes(q)
    );
  }, [query, enrollments]);

  const downloadCsv = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${course?.slug || "course"}-enrollments.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (!course) return <div className="text-[#4A4D57]">Loading…</div>;

  return (
    <div className="space-y-8 vp-fade-up max-w-5xl">
      <div>
        <Link to="/app/courses" className="inline-flex items-center gap-2 text-sm text-[#4A4D57] hover:text-[#0B5CFF]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to courses
        </Link>
        <div className="flex items-start justify-between gap-3 mt-3 flex-wrap">
          <div>
            <div className="vp-label">Course</div>
            <h1 className="font-display text-3xl font-bold mt-1">{course.title}</h1>
            <p className="text-[#4A4D57] mt-2 max-w-2xl">{course.description}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={copy} className="vp-btn-ghost text-sm inline-flex items-center gap-2" data-testid="course-copy-link">
              <Copy className="w-4 h-4" /> Copy link
            </button>
            <a href={`/c/${course.slug}`} target="_blank" rel="noreferrer" className="vp-btn-primary text-sm inline-flex items-center gap-2" data-testid="course-preview-link">
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
            <button onClick={remove} className="p-2.5 text-[#4A4D57] hover:text-[#B00020] hover:bg-[#FDE8EA] rounded-lg" title="Delete" data-testid="course-delete-btn">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        <div className="vp-kpi" data-testid="course-kpi-enrollments">
          <div className="vp-label">Enrollments</div>
          <div className="font-display text-2xl font-bold mt-1">{enrollStats.count}</div>
        </div>
        <div className="vp-kpi" data-testid="course-kpi-paid">
          <div className="vp-label">Paid</div>
          <div className="font-display text-2xl font-bold mt-1">{enrollStats.paid_count}</div>
        </div>
        <div className="vp-kpi" data-testid="course-kpi-revenue">
          <div className="vp-label">Revenue</div>
          <div className="font-display text-2xl font-bold mt-1">{fmtINR(enrollStats.revenue)}</div>
        </div>
        <div className="vp-kpi" data-testid="course-kpi-modules">
          <div className="vp-label">Modules</div>
          <div className="font-display text-2xl font-bold mt-1">{modules.length}</div>
        </div>
      </div>

      <div>
        <div className="vp-label mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-[#0B5CFF]" /> Modules</div>
        <div className="space-y-3">
          {modules.length === 0 && (
            <div className="vp-card p-6 text-[#4A4D57]">No modules attached yet.</div>
          )}
          {modules.map((m, i) => (
            <div key={m.id} className="vp-card p-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-lg bg-[#0B5CFF]/10 flex items-center justify-center font-mono text-[#0B5CFF] text-sm">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{m.title}</div>
                <div className="text-xs text-[#747680] mt-0.5">
                  {course.schedule_kind === "drip" ? "Unlocks" : "Live"} · {new Date(m.scheduled_at).toLocaleString("en-IN")}
                </div>
              </div>
              <Link to={`/app/webinars/${m.id}`} className="text-xs text-[#0B5CFF] hover:underline">Open →</Link>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="vp-label flex items-center gap-2"><Users className="w-4 h-4 text-[#0B5CFF]" /> Enrollments ({enrollments.length})</div>
          <div className="flex gap-2">
            <input
              className="vp-input text-xs max-w-xs"
              placeholder="Search name / email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="course-enroll-search"
            />
            <button
              onClick={downloadCsv}
              disabled={filtered.length === 0}
              className="vp-btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="course-enroll-csv"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>

        {enrollments.length === 0 ? (
          <div className="vp-card p-6 text-[#4A4D57] text-sm">No enrollments yet. Share the landing link above to start selling.</div>
        ) : (
          <div className="vp-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F6F7FA] text-left text-[11px] uppercase tracking-widest text-[#747680]">
                    <th className="p-3 font-semibold">Name</th>
                    <th className="p-3 font-semibold">Contact</th>
                    <th className="p-3 font-semibold">Amount</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold">Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t border-[#E4E6EC]" data-testid="course-enroll-row">
                      <td className="p-3 font-medium">{r.name}</td>
                      <td className="p-3 text-[#4A4D57]">
                        <div className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {r.email}</div>
                        {r.whatsapp && (
                          <div className="inline-flex items-center gap-1.5 ml-3 text-xs">
                            <Phone className="w-3 h-3" /> {r.country_code || ""} {r.whatsapp}
                          </div>
                        )}
                      </td>
                      <td className="p-3">{r.amount ? fmtINR(r.amount) : "Free"}</td>
                      <td className="p-3">
                        {r.payment_status === "paid" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E7F5EC] text-[#1E8E3E] text-xs font-semibold">
                            <CheckCircle2 className="w-3 h-3" /> Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF3CD] text-[#8A6D3B] text-xs font-semibold">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-[#747680]">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
